import SecureRequest, { apiGet, apiPost } from "../utils/securerequest";

/** @typedef {"MMS"|"USER_DOC"|"PROFILE"|"ASSESSMENT_BATCH"} UploadType */

/**
 * Map a legacy S3 bucket name (what callers passed to aws-sdk) to the backend
 * upload `type`. Unknown/absent buckets default to USER_DOC.
 * @param {string} [bucket]
 * @returns {UploadType}
 */
export const uploadTypeForBucket = (bucket) => {
  if (bucket && bucket === process.env.AWS_SMS_FILE_BUCKET) return "MMS";
  if (bucket && bucket === process.env.AWS_ASSESSMENT_BATCH_DOCS_BUCKET) return "ASSESSMENT_BATCH";
  return "USER_DOC";
};

/**
 * Proxy a document upload through an-api to S3 (replaces direct aws-sdk S3 PutObject).
 * The backend chooses the bucket from `type` and performs the upload.
 * @param {Object} opts
 * @param {File} opts.file
 * @param {string} opts.key - full S3 object key
 * @param {UploadType} opts.type
 * @param {string} [opts.contentType]
 * @param {"public-read"|"private"|"authenticated-read"} [opts.acl] - not allowed for MMS
 */
export const uploadUserDocumentToS3Async = async ({ file, key, type, contentType, acl }) => {
  try {
    return await apiPost("/v3/documents/upload/user-doc", file, {
      headers: {
        "Content-Type": "application/octet-stream",
        "x-upload-key": key,
        "x-upload-type": type,
        "x-upload-content-type": contentType || "application/octet-stream",
        ...(acl ? { "x-upload-acl": acl } : {}),
      },
    });
  } catch (error) {
    const safeError = new Error("Document upload failed");
    safeError.name = "DocumentUploadError";
    if (error?.code) safeError.code = error.code;
    if (typeof error?.response?.status === "number") {
      safeError.status = error.response.status;
    }
    throw safeError;
  }
};

/**
 * Copy an object from the MMS bucket to the user-doc bucket via an-api
 * (replaces direct aws-sdk S3 CopyObject).
 * @param {Object} opts
 * @param {string} opts.sourceKey
 * @param {string} opts.destinationKey
 */
export const copyMmsToUserDocAsync = async ({ sourceKey, destinationKey }) => {
  return await apiPost("/v3/documents/copy-mms", { sourceKey, destinationKey });
};

/**
 * Get a presigned GET URL for a user-doc object by its S3 key (cognitoId +
 * filename), via an-api. Replaces direct aws-sdk s3.getSignedUrl. Supports an
 * optional S3 versionId, and `keyIsFullPath` when `filename` is already the
 * full object key (versioned documents).
 * @param {Object} opts
 * @param {string} [opts.cognitoId]
 * @param {string} opts.filename
 * @param {string} [opts.versionId]
 * @param {boolean} [opts.keyIsFullPath]
 * @param {number} [opts.expiresInSeconds]
 * @returns {Promise<import('axios').AxiosResponse<{ url: string }>>}
 */
export const getSignedUrlByKeyAsync = async ({
  cognitoId,
  filename,
  versionId,
  keyIsFullPath,
  expiresInSeconds,
}) => {
  return await apiGet("/v3/documents/signed-url-by-key", {
    params: {
      ...(cognitoId ? { cognitoId } : {}),
      filename,
      ...(versionId ? { versionId } : {}),
      ...(keyIsFullPath ? { keyIsFullPath: "true" } : {}),
      ...(expiresInSeconds ? { expiresInSeconds } : {}),
    },
  });
};

/**
 * List S3 versions of a user document via an-api (replaces direct
 * aws-sdk s3.listObjectVersions).
 * @param {Object} opts
 * @param {string} opts.cognitoId
 * @param {string} opts.filename
 * @returns {Promise<import('axios').AxiosResponse<{ Versions: Array<{ Key: string, VersionId: string, LastModified: string }> }>>}
 */
export const listUserDocVersionsAsync = async ({ cognitoId, filename }) => {
  return await apiGet("/v3/documents/versions", { params: { cognitoId, filename } });
};

export const fetchDocumentSignatures = async (documentId) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  const url = `/v3/documents/${documentId}/document-signed`;

  return await Request.get(url);
};

export const fetchPandaDocStatus = async (documentId) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  const url = `/v3/documents/${documentId}/pandadoc-status`;

  return await Request.get(url);
};

export const fetchPandaDocSessionForDocument = async (documentId) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  const url = `/v3/documents/${documentId}/panda-doc-session`;

  return await Request.get(url);
};
